import { useEffect, useState } from "react";
import { Loader2, MessageCircle, Headphones, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";

export default function Support() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        // Nur echte TSU-Mitarbeiter (Admins + Support-Team)
        const supporters = data.filter(
          (u) => u.is_support || u.role === "admin" || u.role === "support"
        );
        setStaff(supporters);
      } catch (err) {
        console.error("Failed to load support staff", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="support-page" className="space-y-6">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">
          Direkter Draht
        </p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
          Mitarbeiter <Headphones className="w-7 h-7 text-[#4DBFFF]" />
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-xl">
          Unser TSU-Team hilft dir persönlich bei Fragen zu Aufträgen, Vergütung, Schadensabwicklung
          oder Papieren. Schreib direkt einem Mitarbeiter – auch ohne vorherige Freundschaftsanfrage.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Team…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((u) => {
            const isAdmin = u.role === "admin";
            return (
              <div
                key={u.id}
                data-testid={`support-card-${u.id}`}
                className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-5 hover:border-emerald-500/40 hover:shadow-[0_8px_40px_rgba(16,185,129,0.15)] transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={fileUrl(u.avatar_url)}
                      alt={u.name}
                      className="w-14 h-14 rounded-full object-cover border border-slate-200 bg-slate-100/70"
                    />
                    <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-[#FFFFFF] shadow-[0_0_10px_rgba(52,211,153,0.6)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-slate-900 truncate flex items-center gap-1.5 flex-wrap">
                      {u.name}
                      {isAdmin ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1D5BFF]/25 text-[#4DBFFF] border border-[#1D5BFF]/40 font-bold inline-flex items-center gap-1">
                          <Shield className="w-2.5 h-2.5" /> Admin
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold">
                          TSU Support
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-emerald-300 flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Aktiv jetzt
                    </div>
                  </div>
                </div>
                {u.bio && <p className="text-sm text-slate-700 mt-3 line-clamp-3">{u.bio}</p>}
                <button
                  onClick={() => navigate(`/nachrichten/${u.id}`)}
                  data-testid={`support-message-${u.id}`}
                  className="btn-brand mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full"
                >
                  <MessageCircle className="w-4 h-4" /> Nachricht schreiben
                </button>
              </div>
            );
          })}
          {staff.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-12">
              Aktuell ist kein Mitarbeiter online. Bitte versuche es später erneut.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
